import * as React from "react";

/**
 * Shared DealerCards helpers + Square-like brand badges.
 */

/** Max saved payment cards per dealer — change here to adjust everywhere. */
export const MAX_SAVED_CARDS = 5;

/**
 * Max years ahead we accept client-side.
 * Square docs do not publish a hard ceiling (sandbox success path accepts
 * any future month/year; sandbox 01/40 is a CreatePayment error-injection
 * value, not a year limit). Cap at 20 from typical issuer windows
 * (usually 2–5 years; payment forms commonly allow ~10–20).
 */
export const MAX_CARD_EXPIRATION_YEARS_AHEAD = 20;

export const CARD_EXPIRATION_ERROR_MESSAGE = "Invalid expiration date.";

/**
 * Normalize YY → YYYY (assume 2000–2099). Leave YYYY as-is.
 */
function normalizeCardExpYear(expYear) {
  const y = Number(expYear);
  if (!Number.isFinite(y) || y <= 0) return null;
  if (y < 100) return 2000 + Math.trunc(y);
  return Math.trunc(y);
}

/**
 * Reject past expirations and far-future years that are almost certainly
 * typos (Square tokenization may still accept them).
 */
export function isCardExpirationAcceptable(expMonth, expYear) {
  const month = Number(expMonth);
  const year = normalizeCardExpYear(expYear);
  if (!Number.isFinite(month) || month < 1 || month > 12 || year == null) {
    return false;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }
  if (year > currentYear + MAX_CARD_EXPIRATION_YEARS_AHEAD) {
    return false;
  }
  return true;
}

export function parseIsDefault(value) {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  return s === "yes" || s === "true" || s === "y";
}

export function normalizeCardsResponse(response) {
  if (response == null) {
    return [];
  }
  const raw = response.data ?? response;
  let list = [];

  if (Array.isArray(raw?.cards)) {
    list = raw.cards;
  } else if (Array.isArray(raw?.data?.cards)) {
    list = raw.data.cards;
  } else if (Array.isArray(raw)) {
    list = raw;
  } else if (Array.isArray(raw?.data)) {
    list = raw.data;
  } else if (
    raw &&
    typeof raw === "object" &&
    (raw.CardLast4 != null ||
      raw.last4 != null ||
      raw.Last4 != null ||
      raw.PaymentSourceID != null ||
      raw.SquareCardID != null ||
      raw.card_id != null)
  ) {
    list = [raw];
  }

  return list.map((c) => {
    const last4 = c.CardLast4 ?? c.last4 ?? c.Last4 ?? c.last_four ?? "";
    const brand = c.CardBrand ?? c.brand ?? c.Brand ?? c.card_brand ?? "";
    const expMonth = c.ExpMonth ?? c.exp_month ?? 0;
    const expYear = c.ExpYear ?? c.exp_year ?? 0;
    const isDefault = parseIsDefault(
      c.IsDefault ?? c.is_default ?? c.isDefault,
    );
    const id =
      c.DealerCardNo ??
      c.dealer_card_no ??
      c.CardNo ??
      c.card_no ??
      c.id ??
      c.pk ??
      null;
    return {
      DealerCardNo: id,
      DealerNo: c.DealerNo ?? c.dealer_no ?? c.dealer_id ?? null,
      SquareCardID:
        c.SquareCardID ??
        c.PaymentSourceID ??
        c.payment_source_id ??
        c.card_id ??
        null,
      PaymentSourceID:
        c.SquareCardID ??
        c.PaymentSourceID ??
        c.payment_source_id ??
        c.card_id ??
        null,
      SquareCustomerID:
        c.SquareCustomerID ??
        c.PaymentCustomerID ??
        c.square_customer_id ??
        c.customer_id ??
        null,
      CardHolderName:
        c.CardHolderName ?? c.cardholder_name ?? c.CardholderName ?? "",
      CardLast4: last4 != null && last4 !== "" ? String(last4) : "",
      CardBrand: brand != null && brand !== "" ? String(brand) : "",
      ExpMonth: Number(expMonth) || 0,
      ExpYear: Number(expYear) || 0,
      IsDefault: isDefault ? 1 : 0,
    };
  });
}

export const AUTOBILLING_CARD_REQUIRED_MESSAGE =
  "At least one payment method must be added.";

/** Normalize AutoBilling from OEM dealers list (`auto_billing`: 0|1). */
export function normalizeAutoBilling(value) {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? 1 : 0;
  const n = Number(value);
  if (Number.isFinite(n)) return n === 1 ? 1 : 0;
  const s = String(value).trim().toLowerCase();
  if (s === "yes" || s === "true") return 1;
  if (s === "no" || s === "false") return 0;
  return null;
}

function paymentApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

function authHeadersFromSession() {
  const token = JSON.parse(sessionStorage.getItem("userData"));
  return { Authorization: `Bearer ${token?.access_token}` };
}

/** Returns true when the dealer has at least one saved card. */
export async function fetchDealerHasSavedCards(dealerNo) {
  if (dealerNo == null || dealerNo === "") return false;
  const base = paymentApiBase();
  const url = `${base}/api/dealers/${dealerNo}/cards/`;
  const { default: axios } = await import("axios");
  const res = await axios.get(url, { headers: authHeadersFromSession() });
  return normalizeCardsResponse(res.data).length > 0;
}


export function brandKeyFromCard(card) {
  const raw = String(card?.CardBrand ?? "")
    .toLowerCase()
    .replace(/[_\s-]+/g, " ")
    .trim();
  if (!raw) return "generic";
  if (raw.includes("visa")) return "visa";
  if (raw.includes("master")) return "mastercard";
  if (raw.includes("amex") || raw.includes("american express")) return "amex";
  if (raw.includes("discover")) return "discover";
  if (raw.includes("diner")) return "diners";
  if (raw.includes("jcb")) return "jcb";
  if (raw.includes("union") || raw.includes("unionpay") || raw.includes("china"))
    return "unionpay";
  return "generic";
}

export function formatExpShort(card) {
  if (!card?.ExpMonth || !card?.ExpYear) return "";
  const mm = String(card.ExpMonth).padStart(2, "0");
  const yy = String(card.ExpYear).slice(-2);
  return `Exp ${mm}/${yy}`;
}

export function formatExpiresOn(card) {
  if (!card?.ExpMonth || !card?.ExpYear) return "";
  const mm = String(card.ExpMonth).padStart(2, "0");
  const yy = String(card.ExpYear).slice(-2);
  return `Expires on ${mm}/${yy}`;
}

/** Square-like payment network badges — clean flat marks. */
export function CardBrandIcon({ brandKey, className = "shrink-0" }) {
  const wrap =
    "inline-flex h-[28px] w-[42px] items-center justify-center rounded-[3px] border border-[#e4e7ec] bg-white overflow-hidden " +
    className;
  const k = brandKey || "generic";
  const uid = React.useId().replace(/:/g, "");

  if (k === "visa") {
    return (
      <span className={wrap} aria-hidden title="Visa">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <rect width="48" height="32" fill="#1A1F71" />
          <text
            x="24"
            y="21"
            textAnchor="middle"
            fill="#fff"
            fontSize="13"
            fontStyle="italic"
            fontWeight="800"
            fontFamily="Arial, Helvetica, sans-serif"
            letterSpacing="1"
          >
            VISA
          </text>
        </svg>
      </span>
    );
  }

  if (k === "mastercard") {
    return (
      <span className={wrap} aria-hidden title="Mastercard">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <rect width="48" height="32" fill="#000" />
          <circle cx="18" cy="16" r="8" fill="#EB001B" />
          <circle cx="30" cy="16" r="8" fill="#F79E1B" />
          <path
            fill="#FF5F00"
            d="M24 9.4a8 8 0 0 1 0 13.2 8 8 0 0 1 0-13.2z"
          />
        </svg>
      </span>
    );
  }

  if (k === "amex") {
    return (
      <span className={wrap} aria-hidden title="American Express">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <rect width="48" height="32" fill="#016FD0" />
          <text
            x="24"
            y="20.5"
            textAnchor="middle"
            fill="#fff"
            fontSize="10"
            fontWeight="700"
            fontFamily="Arial, Helvetica, sans-serif"
            letterSpacing="0.5"
          >
            AMEX
          </text>
        </svg>
      </span>
    );
  }

  if (k === "discover") {
    return (
      <span className={wrap} aria-hidden title="Discover">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <rect width="48" height="32" fill="#fff" />
          <text
            x="4"
            y="19.5"
            fill="#231F20"
            fontSize="6.5"
            fontWeight="700"
            fontFamily="Arial, Helvetica, sans-serif"
          >
            DISCOVER
          </text>
          <circle cx="40" cy="16" r="7" fill="#F47216" />
        </svg>
      </span>
    );
  }

  if (k === "diners") {
    return (
      <span className={wrap} aria-hidden title="Diners Club">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <rect width="48" height="32" fill="#fff" />
          <ellipse cx="24" cy="16" rx="14" ry="11" fill="#0079BE" />
          <ellipse cx="24" cy="16" rx="10.5" ry="8.2" fill="#fff" />
          <path
            fill="#0079BE"
            d="M19.5 10.2v11.6c-2.7-1.4-4.5-3.9-4.5-5.8s1.8-4.4 4.5-5.8zm9 0c2.7 1.4 4.5 3.9 4.5 5.8s-1.8 4.4-4.5 5.8V10.2z"
          />
        </svg>
      </span>
    );
  }

  if (k === "jcb") {
    return (
      <span className={wrap} aria-hidden title="JCB">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <defs>
            <linearGradient id={`${uid}-j`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1F6BBE" />
              <stop offset="100%" stopColor="#0E4C96" />
            </linearGradient>
            <linearGradient id={`${uid}-c`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#E84A3E" />
              <stop offset="100%" stopColor="#C41230" />
            </linearGradient>
            <linearGradient id={`${uid}-b`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2DBE6C" />
              <stop offset="100%" stopColor="#0A8F45" />
            </linearGradient>
          </defs>
          <rect width="48" height="32" fill="#fff" />
          <rect
            x="4"
            y="7"
            width="12"
            height="18"
            rx="2.5"
            fill={`url(#${uid}-j)`}
          />
          <rect
            x="18"
            y="7"
            width="12"
            height="18"
            rx="2.5"
            fill={`url(#${uid}-c)`}
          />
          <rect
            x="32"
            y="7"
            width="12"
            height="18"
            rx="2.5"
            fill={`url(#${uid}-b)`}
          />
          <text
            x="10"
            y="19"
            textAnchor="middle"
            fill="#fff"
            fontSize="8"
            fontWeight="800"
            fontFamily="Arial, Helvetica, sans-serif"
          >
            J
          </text>
          <text
            x="24"
            y="19"
            textAnchor="middle"
            fill="#fff"
            fontSize="8"
            fontWeight="800"
            fontFamily="Arial, Helvetica, sans-serif"
          >
            C
          </text>
          <text
            x="38"
            y="19"
            textAnchor="middle"
            fill="#fff"
            fontSize="8"
            fontWeight="800"
            fontFamily="Arial, Helvetica, sans-serif"
          >
            B
          </text>
        </svg>
      </span>
    );
  }

  if (k === "unionpay") {
    return (
      <span className={wrap} aria-hidden title="UnionPay">
        <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
          <rect width="48" height="32" fill="#fff" />
          <path
            fill="#E21836"
            d="M4 6h14l-4 20H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"
          />
          <path fill="#00447C" d="M16 6h16l-4 20H12z" />
          <path
            fill="#007B5F"
            d="M30 6h14a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H26z"
          />
          <text
            x="24"
            y="19"
            textAnchor="middle"
            fill="#fff"
            fontSize="5.5"
            fontWeight="700"
            fontFamily="Arial, Helvetica, sans-serif"
          >
            UnionPay
          </text>
        </svg>
      </span>
    );
  }

  return (
    <span className={wrap} aria-hidden title="Card">
      <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
        <rect width="48" height="32" fill="#F8FAFC" />
        <rect x="0" y="9" width="48" height="4" fill="#CBD5E1" />
        <rect x="6" y="20" width="12" height="3" rx="1" fill="#E2E8F0" />
        <rect x="30" y="20" width="12" height="3" rx="1" fill="#E2E8F0" />
      </svg>
    </span>
  );
}
